import { useEffect, useState, useCallback } from "react";
import { Mail, Trash2, RefreshCw, X, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import {
  adminListAbandonedCarts, adminSendCartRecovery,
} from "../../lib/api";
import { ExportCsvButton, downloadCsv } from "./shared";

export function AbandonedCartsTab({ pw }) {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [threshold, setThreshold] = useState(30);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListAbandonedCarts(pw, threshold);
      setCarts(res.carts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load abandoned carts");
    } finally {
      setLoading(false);
    }
  }, [pw, threshold]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSend = async (email) => {
    if (!window.confirm(`Send WELCOME10 recovery email to ${email}?`)) return;
    setSendingEmail(email);
    try {
      await adminSendCartRecovery(email, pw);
      toast.success(`Recovery email sent to ${email}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send recovery email");
    } finally {
      setSendingEmail(null);
    }
  };

  return (
    <div data-testid="abandoned-carts-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl uppercase">Abandoned Carts</h2>
          <p className="font-mono text-xs text-zinc-500 mt-1">
            Customers who initiated checkout but didn't complete payment.
            Auto-recovery runs hourly via scheduler.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-1">
              THRESHOLD (MIN)
            </label>
            <input
              type="number" min="5" max="1440" value={threshold}
              onChange={(e) => setThreshold(Math.max(5, parseInt(e.target.value, 10) || 30))}
              data-testid="abandoned-threshold"
              className="w-24 bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
            />
          </div>
          <button
            onClick={refresh}
            data-testid="abandoned-refresh"
            className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-10"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="surface-card p-4 mb-6 flex items-center gap-3">
        <ShoppingCart size={20} className="text-[var(--tc-red)]" />
        <div>
          <div className="font-display text-2xl">{carts.length}</div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">
            Recoverable carts &gt; {threshold} min old
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">Loading…</div>
      ) : carts.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <ShoppingCart size={32} className="mx-auto mb-3 text-zinc-700" />
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">
            No abandoned carts. Solid checkout flow!
          </div>
        </div>
      ) : (
        <div className="surface-card overflow-x-auto" data-testid="abandoned-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#222]">
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">CUSTOMER</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">ITEMS</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">TOTAL</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">LAST SEEN</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">STATUS</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4 text-right">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {carts.map((c) => {
                const sent = c.recovery_email_sent;
                return (
                  <tr key={c.customer_email} className="border-b border-[#222] last:border-b-0">
                    <td className="p-4">
                      <div className="font-ui text-sm">{c.customer_name || "—"}</div>
                      <div className="font-mono text-xs text-zinc-500">{c.customer_email}</div>
                    </td>
                    <td className="p-4 font-mono text-sm">{c.last_items_count || 0}</td>
                    <td className="p-4 font-mono text-sm">${Number(c.last_total || 0).toFixed(2)}</td>
                    <td className="p-4 font-mono text-xs text-zinc-400">
                      {c.last_seen_at ? new Date(c.last_seen_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-4">
                      {sent ? (
                        <span className="font-mono text-[10px] tracking-widest text-emerald-400 uppercase">
                          ✓ Sent {c.recovery_email_auto ? "(auto)" : ""}
                        </span>
                      ) : (
                        <span className="font-mono text-[10px] tracking-widest text-amber-400 uppercase">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleSend(c.customer_email)}
                        disabled={!!sent || sendingEmail === c.customer_email}
                        data-testid={`send-recovery-${c.customer_email}`}
                        className="btn-tc-primary inline-flex items-center gap-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Mail size={12} />
                        {sendingEmail === c.customer_email ? "Sending…" : sent ? "Sent" : "Send Recovery"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ===== NEWSLETTER SUBSCRIBERS TAB =====

export default AbandonedCartsTab;
